import { Title } from '@mantine/core';
import { createReference, getReferenceString } from '@medplum/core';
import {
  Encounter,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Reference,
} from '@medplum/fhirtypes';
import { useMedplum, usePrevious, useResource } from '@medplum/react-hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildInitialResponse,
  buildInitialResponseItem,
  evaluateCalculatedExpressionsInQuestionnaire,
  Form,
  getNumberOfPages,
  isQuestionEnabled,
  mergeUpdatedItems,
} from '@medplum/react';
import { QuestionnaireFormContext } from './QuestionnaireForm.context';
import { QuestionnairePageSequence } from './QuestionnaireFormComponents/QuestionnaireFormPageSequence';
import { notifications } from '@mantine/notifications';
import { SignatureValidation } from './source-offset/SignatureValidation';
import { pruneDisabledAnswers } from '@/utils/prune-questionnaire-answers';
import { RENDERING_STYLE_EXTENSION_URL } from '@consts/systems.consts';

export interface QuestionnaireFormProps {
  readonly questionnaire: Questionnaire | Reference<Questionnaire>;
  readonly questionnaireResponse?:
    | QuestionnaireResponse
    | Reference<QuestionnaireResponse>;
  readonly subject?: Reference;
  readonly encounter?: Reference<Encounter>;
  readonly source?: QuestionnaireResponse['source'];
  readonly disablePagination?: boolean;
  readonly excludeButtons?: boolean;
  readonly submitButtonText?: string;
  readonly onChange?: (response: QuestionnaireResponse) => void;
  readonly onSubmit?: (response: QuestionnaireResponse) => void;
}

export function QuestionnaireForm(
  props: QuestionnaireFormProps
): JSX.Element | null {
  const medplum = useMedplum();
  const { subject, source: sourceFromProps } = props;
  const questionnaire = useResource(props.questionnaire);
  const prevQuestionnaire = usePrevious(questionnaire);
  const questionnaireResponse = useResource(props.questionnaireResponse);
  const [response, setResponse] = useState<QuestionnaireResponse | undefined>(
    questionnaireResponse
  );
  const [activePage, setActivePage] = useState(0);
  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;

  const onSubmitRef = useRef(props.onSubmit);
  onSubmitRef.current = props.onSubmit;

  useEffect(() => {
    if (questionnaireResponse?.item && questionnaire) {
      // For repeatable groups, we need to preserve the original response structure
      // instead of using buildInitialResponse which merges items with the same linkId
      const response = preserveRepeatableGroupsInResponse(
        questionnaire,
        questionnaireResponse
      );
      const evaluatedResponse = evaluateAndMergeResponseItems(
        response,
        questionnaire
      );
      setResponse(evaluatedResponse);
    } else {
      if (
        questionnaire &&
        getQuestionnaireIdentity(prevQuestionnaire) ===
          getQuestionnaireIdentity(questionnaire)
      ) {
        return;
      }
      // throw out the existing response and start over
      setResponse(
        questionnaire ? buildInitialResponse(questionnaire) : undefined
      );
    }
  }, [questionnaireResponse, questionnaire, prevQuestionnaire]);

  useEffect(() => {
    if (response && onChangeRef.current) {
      try {
        onChangeRef.current(response);
      } catch (e) {
        console.error('Error invoking QuestionnaireForm.onChange callback', e);
      }
    }
  }, [response]);

  const setItems = useCallback(
    (
      newResponseItems: QuestionnaireResponseItem | QuestionnaireResponseItem[]
    ): void => {
      setResponse((prevResponse) => {
        const updated = updateResponseItems(
          prevResponse,
          newResponseItems,
          questionnaire
        );

        return updated;
      });
    },
    [questionnaire]
  );

  const handleSubmit = useCallback(() => {
    try {
      if (!questionnaire || !response) {
        return;
      }

      // Clean up disabled answers before submit
      const pruned: QuestionnaireResponse = {
        ...response,
        item: pruneDisabledAnswers(questionnaire.item, response.item, response),
      };
      SignatureValidation(questionnaire, pruned);

      const onSubmit = onSubmitRef.current;
      if (onSubmit) {
        let source = sourceFromProps;
        if (!source) {
          const profile = medplum.getProfile();
          if (profile) {
            source = createReference(profile);
          }
        }
        onSubmit({
          ...pruned,
          questionnaire: questionnaire.url ?? getReferenceString(questionnaire),
          subject,
          source,
          authored: new Date().toISOString(),
          status: 'completed',
        });
      }
    } catch (e) {
      notifications.show({
        message: e instanceof Error ? e.message : 'Error submitting form',
        color: 'red',
      });
    }
  }, [medplum, questionnaire, response, subject, sourceFromProps]);

  //----------------------------------------------------------------------------
  function getByLinkId(
    responseItems: QuestionnaireResponseItem[] | undefined,
    linkId: string
  ): QuestionnaireResponseItemAnswer[] | undefined {
    if (!responseItems) {
      return undefined;
    }

    for (const response of responseItems) {
      if (response.linkId === linkId) {
        return response.answer;
      }
      if (response.item) {
        const nestedAnswer = getByLinkId(response.item, linkId);
        if (nestedAnswer) {
          return nestedAnswer;
        }
      }
    }

    return undefined;
  }

  function checkForQuestionEnabled(
    item: QuestionnaireItem,
    contextResponse?: QuestionnaireResponse
  ): boolean {
    // Early return if no response is available
    if (!response) {
      return false;
    }

    // Determine which response context to use for enableWhen evaluation
    let responseToUse: QuestionnaireResponse;

    if (item.enableWhen) {
      // Check if any enableWhen condition references a question that exists in the current context
      const hasLocalReference = item.enableWhen.some((condition) => {
        const answers = getByLinkId(
          contextResponse?.item,
          condition.question as string
        );
        return answers !== undefined;
      });

      // If we have local references, use the context response (for repeatable groups)
      // Otherwise, use the full response (for groups that reference root-level questions)
      if (hasLocalReference && contextResponse) {
        responseToUse = contextResponse;
      } else {
        responseToUse = response;
      }
    } else {
      // No enableWhen conditions, use the provided context
      responseToUse = contextResponse || response;
    }

    const enabled = isQuestionEnabled(item, responseToUse);

    if (!enabled) {
      return false;
    }

    // Extra safeguard: if item has multiple enableWhen with "all",
    // make sure all referenced questions actually have answers.
    if (item.enableWhen && item.enableBehavior === 'all') {
      for (const condition of item.enableWhen) {
        const answers = getByLinkId(
          responseToUse?.item,
          condition.question as string
        );
        if (!answers || answers.length === 0) {
          return false; // unanswered → not satisfied
        }
      }
    }

    return true;
  }

  //----------------------------------------------------------------------------
  if (!questionnaire || !response) {
    return null;
  }

  const numberOfPages = getNumberOfPages(questionnaire);
  const nextStep = (): void => setActivePage((current) => current + 1);
  const prevStep = (): void => setActivePage((current) => current - 1);

  //----------------------------------------------------------------------------
  /**
   * extract styling options for the questionnaire's title
   */
  const titleStyling =
    questionnaire.extension?.find(
      (ext) => ext.url === RENDERING_STYLE_EXTENSION_URL
    )?.valueString || '';

  //----------------------------------------------------------------------------
  return (
    <QuestionnaireFormContext.Provider
      value={{ subject: props.subject, encounter: props.encounter }}
    >
      <Form testid="questionnaire-form" onSubmit={handleSubmit}>
        {questionnaire.title && (
          <Title className={titleStyling}>
            {questionnaire.title.split('\n').map((line) => (
              <>
                {line}
                <br />
              </>
            ))}
          </Title>
        )}
        <QuestionnairePageSequence
          items={questionnaire.item ?? []}
          response={response}
          onChange={setItems}
          renderPages={!props.disablePagination && numberOfPages > 1}
          activePage={activePage}
          numberOfPages={numberOfPages}
          excludeButtons={props.excludeButtons}
          submitButtonText={props.submitButtonText}
          checkForQuestionEnabled={checkForQuestionEnabled}
          nextStep={nextStep}
          prevStep={prevStep}
        />
      </Form>
    </QuestionnaireFormContext.Provider>
  );
}

function mergeIndividualItems(
  prevItem: QuestionnaireResponseItem,
  newItem: QuestionnaireResponseItem
): QuestionnaireResponseItem {
  // Recursively merge the nested items based on their ids.
  const mergedNestedItems = mergeItems(prevItem.item ?? [], newItem.item ?? []);

  return {
    ...newItem,
    item: mergedNestedItems.length > 0 ? mergedNestedItems : undefined,
    answer:
      newItem.answer && newItem.answer.length > 0
        ? newItem.answer
        : prevItem.answer,
  };
}

function mergeItems(
  prevItems: QuestionnaireResponseItem[],
  newItems: QuestionnaireResponseItem[]
): QuestionnaireResponseItem[] {
  // Handle removal operation
  if (newItems.length === 0) {
    return [];
  }

  // Handle replacement operation (same linkIds but different counts)
  if (isReplacementOperation(prevItems, newItems)) {
    return newItems;
  }

  // Handle partial removal operation
  const partialRemovalResult = handlePartialRemoval(prevItems, newItems);
  if (partialRemovalResult) {
    return partialRemovalResult;
  }

  // Standard merge operation
  return performStandardMerge(prevItems, newItems);
}

const isReplacementOperation = (
  prevItems: QuestionnaireResponseItem[],
  newItems: QuestionnaireResponseItem[]
): boolean => {
  if (prevItems.length === 0 || newItems.length === 0) {
    return false;
  }

  const prevLinkIds = new Set(prevItems.map((item) => item.linkId));
  const newLinkIds = new Set(newItems.map((item) => item.linkId));

  const hasSameLinkIds =
    prevLinkIds.size === newLinkIds.size &&
    [...prevLinkIds].every((id) => newLinkIds.has(id));

  return hasSameLinkIds && prevItems.length !== newItems.length;
};

const handlePartialRemoval = (
  prevItems: QuestionnaireResponseItem[],
  newItems: QuestionnaireResponseItem[]
): QuestionnaireResponseItem[] | null => {
  if (newItems.length >= prevItems.length) {
    return null;
  }

  const newLinkIdSet = new Set(newItems.map((item) => item.linkId));

  // Check if all newItems have the same linkId
  if (newLinkIdSet.size !== 1) {
    return null;
  }

  const newLinkId = [...newLinkIdSet][0];
  const prevItemsWithSameLinkId = prevItems.filter(
    (item) => item.linkId === newLinkId
  );

  // Check if this is a partial removal of items with the same linkId
  if (prevItemsWithSameLinkId.length > newItems.length) {
    const otherItems = prevItems.filter((item) => item.linkId !== newLinkId);
    return [...otherItems, ...newItems];
  }

  return null;
};

const performStandardMerge = (
  prevItems: QuestionnaireResponseItem[],
  newItems: QuestionnaireResponseItem[]
): QuestionnaireResponseItem[] => {
  const result: QuestionnaireResponseItem[] = [];
  const usedIds = new Set<string>();

  // Update existing items
  for (const prevItem of prevItems) {
    const newItem = newItems.find((item) => item.id === prevItem.id);
    if (newItem) {
      result.push(mergeIndividualItems(prevItem, newItem));
      usedIds.add(newItem.id as string);
    } else {
      result.push(prevItem);
    }
  }

  // Add new items
  for (const newItem of newItems) {
    if (!usedIds.has(newItem.id as string)) {
      result.push(newItem);
    }
  }

  return result;
};

function getQuestionnaireIdentity(
  questionnaire: Questionnaire | undefined
): Questionnaire | string | undefined {
  return questionnaire?.id || questionnaire;
}

function updateResponseItems(
  prevResponse: QuestionnaireResponse | undefined,
  newResponseItems:
    | QuestionnaireResponseItem
    | QuestionnaireResponseItem[]
    | undefined,
  questionnaire: Questionnaire | undefined
): QuestionnaireResponse {
  if (!newResponseItems) {
    return {
      resourceType: 'QuestionnaireResponse',
      status: 'in-progress',
    };
  }

  const currentItems = prevResponse?.item ?? [];
  const itemsToProcess = Array.isArray(newResponseItems)
    ? newResponseItems
    : [newResponseItems];

  const mergedItems = mergeItems(currentItems, itemsToProcess);

  const tempResponse: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    status: 'in-progress',
    item: mergedItems,
  };

  const updatedItems = evaluateCalculatedExpressionsInQuestionnaire(
    questionnaire?.item ?? [],
    tempResponse
  );
  tempResponse.item = mergeUpdatedItems(mergedItems, updatedItems);

  const finalResponse = evaluateAndMergeResponseItems(
    tempResponse,
    questionnaire
  );
  return finalResponse;
}

function evaluateAndMergeResponseItems(
  tempResponse: QuestionnaireResponse,
  questionnaire: Questionnaire | undefined
): QuestionnaireResponse {
  const updatedItems = evaluateCalculatedExpressionsInQuestionnaire(
    questionnaire?.item ?? [],
    tempResponse
  );
  const mergedItemsWithUpdates = mergeUpdatedItems(
    tempResponse.item ?? [],
    updatedItems
  );
  return {
    resourceType: 'QuestionnaireResponse',
    status: 'in-progress',
    item: mergedItemsWithUpdates,
  };
}

/**
 * Preserve repeatable groups in response by not using buildInitialResponse
 * which merges items with the same linkId
 * @param questionnaire - The questionnaire definition
 * @param existingResponse - The existing questionnaire response
 * @returns A new questionnaire response with preserved repeatable groups
 */
function preserveRepeatableGroupsInResponse(
  questionnaire: Questionnaire,
  existingResponse: QuestionnaireResponse
): QuestionnaireResponse {
  if (!existingResponse?.item || !questionnaire.item) {
    return buildInitialResponse(questionnaire);
  }

  // For repeatable groups, we need to preserve the original structure
  // Let's create a response that preserves multiple items for repeatable groups
  const preservedItems = preserveRepeatableGroupsInItems(
    questionnaire.item,
    existingResponse.item
  );

  return {
    resourceType: 'QuestionnaireResponse',
    status: 'in-progress',
    item: preservedItems,
  };
}

/**
 * Recursively preserve repeatable groups in items
 * @param questionnaireItems - The questionnaire items to process
 * @param existingResponseItems - The existing response items
 * @returns Array of response items with preserved repeatable groups
 */
function preserveRepeatableGroupsInItems(
  questionnaireItems: QuestionnaireItem[],
  existingResponseItems: QuestionnaireResponseItem[]
): QuestionnaireResponseItem[] {
  const result: QuestionnaireResponseItem[] = [];

  for (const questionnaireItem of questionnaireItems) {
    if (questionnaireItem.type === 'group' && questionnaireItem.repeats) {
      // For repeatable groups, preserve all existing items with this linkId
      const existingItemsForGroup = existingResponseItems.filter(
        (item) => item.linkId === questionnaireItem.linkId
      );

      if (existingItemsForGroup.length > 0) {
        // Preserve all existing items for this repeatable group
        for (const existingItem of existingItemsForGroup) {
          const preservedItem = {
            ...existingItem,
            item: existingItem.item
              ? preserveRepeatableGroupsInItems(
                  questionnaireItem.item ?? [],
                  existingItem.item
                )
              : undefined,
          };
          result.push(preservedItem);
        }
      } else {
        // No existing items, create a new one
        const newItem = buildInitialResponseItem(questionnaireItem);
        result.push(newItem);
      }
    } else if (questionnaireItem.type === 'group') {
      // For non-repeatable groups, find the existing item or create new one
      const existingItem = existingResponseItems.find(
        (item) => item.linkId === questionnaireItem.linkId
      );

      if (existingItem) {
        const preservedItem = {
          ...existingItem,
          item: existingItem.item
            ? preserveRepeatableGroupsInItems(
                questionnaireItem.item ?? [],
                existingItem.item
              )
            : undefined,
        };
        result.push(preservedItem);
      } else {
        const newItem = buildInitialResponseItem(questionnaireItem);
        result.push(newItem);
      }
    } else {
      // For non-group items, find the existing item or create new one
      const existingItem = existingResponseItems.find(
        (item) => item.linkId === questionnaireItem.linkId
      );

      if (existingItem) {
        result.push(existingItem);
      } else {
        const newItem = buildInitialResponseItem(questionnaireItem);
        result.push(newItem);
      }
    }
  }

  return result;
}
